import React from 'react'

import { formatDistanceToNow } from 'date-fns'
import Link from 'next/link'
import { HiOutlineClock, HiOutlineSquare3Stack3D } from 'react-icons/hi2'
import { LuCheckCircle2, LuListTodo } from 'react-icons/lu'

import { EmptyValue } from '@/components/common/EmptyValue'
import { OrgPermissionGate } from '@/components/common/PermissionGate'
import { Skeleton } from '@/components/common/Skeleton'
import { useHomeDashboardOpenCreatePursuitDialog } from '@/components/home/dashboard/context'
import { Icon } from '@/components/home/ui/Icon'
import { Surface } from '@/components/home/ui/Surface'

import {
  type ActionItemType,
  useActionItems,
} from '@/hooks/home/useActionItems'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

const MAX_ACTION_ITEMS = 10

const getActionTypeLabel = (type: ActionItemType) => {
  switch (type) {
    case 'task':
      return 'Task'
    case 'pursuit':
      return 'Pursuit'
    default:
      const exhaustiveCheck: never = type
      return exhaustiveCheck
  }
}

interface ActionItemsProps {}

const ActionItemHeader: React.FC = () => {
  return (
    <Surface.Header>
      <Surface.Header.Icon icon={LuCheckCircle2} />
      <Surface.Header.Title>Action Items</Surface.Header.Title>
    </Surface.Header>
  )
}

const ActionItemSkeleton: React.FC = () => {
  return (
    <Surface>
      <ActionItemHeader />
      <Surface.Content className="space-y-4">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="flex items-start">
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        ))}
      </Surface.Content>
    </Surface>
  )
}

const NoActionItems: React.FC = () => {
  const openCreatePursuitDialog = useHomeDashboardOpenCreatePursuitDialog()

  return (
    <>
      <Surface.Placeholder className="py-6">
        <div className="flex flex-col items-center text-center gap-y-2">
          <span className="flex items-center gap-x-2 text-md font-semibold text-primary-900 dark:text-primary-100">
            <LuCheckCircle2
              size={16}
              className="text-green-600 dark:text-green-500"
            />
            You&apos;re all caught up!
          </span>{' '}
          <OrgPermissionGate
            permission="write"
            fallback="replace"
            fallbackComponent={
              <span className="text-[13px] font-medium text-primary-500 dark:text-primary-400">
                No action items at this time.
              </span>
            }
          >
            <span className="text-[13px] font-medium text-primary-500 dark:text-primary-400">
              <button
                className="text-blue-600 dark:text-blue-400 hover:opacity-70"
                onClick={openCreatePursuitDialog}
              >
                Create a new pursuit
              </button>{' '}
              to add more action items.
            </span>
          </OrgPermissionGate>
        </div>
      </Surface.Placeholder>
    </>
  )
}

export const ActionItems: React.FC<ActionItemsProps> = () => {
  const { activeOrgId } = useActiveOrgId()
  const { data: actionItems, isLoading } = useActionItems({
    orgId: activeOrgId,
  })

  if (!actionItems || isLoading) {
    return <ActionItemSkeleton />
  }

  return (
    <Surface className="p-0">
      <ActionItemHeader />
      <Surface.Content>
        <div className="space-y-4">
          {actionItems.data.length > 0 ? (
            actionItems.data.slice(0, MAX_ACTION_ITEMS).map((action) => {
              return (
                <div key={action.id} className="flex items-start gap-3">
                  {action.type === 'task' && (
                    <Icon icon={LuListTodo} variant="yellow" />
                  )}
                  {action.type === 'pursuit' && (
                    <Icon icon={HiOutlineSquare3Stack3D} variant="green" />
                  )}
                  <div className="flex flex-col">
                    <div className="leading-4">
                      <Link
                        href={action.link || ''}
                        className="text-sm font-medium text-primary-900 dark:text-primary-100 hover:opacity-70"
                      >
                        <span>{action.title}</span>
                      </Link>
                    </div>
                    <div className="flex flex-wrap items-center mt-1 text-xs text-primary-500 dark:text-primary-400">
                      <HiOutlineClock
                        size={14}
                        className="flex-shrink-0 mr-1"
                      />
                      <span>
                        {action.due_date ? (
                          `Due ${formatDistanceToNow(action.due_date, { addSuffix: true })}`
                        ) : (
                          <EmptyValue label="No due date" />
                        )}
                      </span>
                      <span className="mx-1">•</span>
                      <span>{getActionTypeLabel(action.type)}</span>
                      <span className="mx-1">•</span>
                      <span>{action.status}</span>
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <NoActionItems />
          )}
        </div>
      </Surface.Content>
    </Surface>
  )
}

ActionItems.displayName = 'Home.ActionItems'
